import type { MfgMaterialMergeResponse } from "@/lib/manufacturing/materialsMergeClient";

type Props = {
  preview: MfgMaterialMergeResponse;
  busy: boolean;
  error: string | null;
  blockers: ReadonlyArray<{ code: string; message: string }>;
  onCancel: () => void;
  onConfirm: () => void;
};

function Stat({
  label,
  value,
  always,
}: {
  label: string;
  value: number | string | undefined;
  always?: boolean;
}) {
  if (value === undefined) return null;
  if (!always && (value === 0 || value === "0")) return null;
  return (
    <li className="flex justify-between gap-3 text-xs text-gray-800 dark:text-zinc-200">
      <span>{label}</span>
      <span className="tabular-nums font-medium">{value}</span>
    </li>
  );
}

export function ManufacturingMaterialMergePreviewModal({
  preview,
  busy,
  error,
  blockers,
  onCancel,
  onConfirm,
}: Props) {
  const fromLabel = `${preview.from_code ?? ""} · ${preview.from_name ?? "discard"}`.trim();
  const toLabel = `${preview.to_code ?? ""} · ${preview.to_name ?? "keeper"}`.trim();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="mfg-material-merge-title"
        className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
      >
        <h2
          id="mfg-material-merge-title"
          className="text-sm font-semibold text-gray-900 dark:text-zinc-100"
        >
          Merge duplicate material
        </h2>
        <p className="mt-2 text-xs text-gray-600 dark:text-zinc-300">
          Merge <span className="font-medium">{fromLabel}</span> into{" "}
          <span className="font-medium">{toLabel}</span>. BOM lines, Part uses, receipts,
          on-hand, and aliases move to the keeper; the discard is permanently deleted.
        </p>
        <ul className="mt-3 space-y-1 rounded border border-gray-100 bg-gray-50 p-2 dark:border-zinc-700 dark:bg-zinc-800">
          <Stat label="BOM lines" value={preview.recipe_lines} always />
          <Stat label="Recipes affected" value={preview.recipes} always />
          <Stat label="Part-use members transferred" value={preview.use_members_transferred} always />
          <Stat label="Part-use members dropped (already on keeper)" value={preview.use_members_dropped} />
          <Stat label="Cost / receipt entries" value={preview.cost_entries} always />
          <Stat label="Issuance events" value={preview.issuance_events} />
          <Stat label="Count lines repointed" value={preview.count_lines_repointed} />
          <Stat label="Count lines dropped (collision)" value={preview.count_lines_dropped} />
          <Stat label="Inter-plant transfers" value={preview.transfers} />
          <Stat label="Aliases repointed" value={preview.aliases_repointed} />
          <Stat label="Aliases dropped (collision)" value={preview.aliases_dropped} />
          <Stat label="On-hand moved" value={preview.on_hand_moved} always />
          <Stat
            label="Keeper on-hand after"
            always
            value={
              preview.keeper_on_hand_after != null
                ? Number(preview.keeper_on_hand_after).toFixed(1)
                : undefined
            }
          />
          <Stat
            label="Keeper WAC after"
            always
            value={
              preview.keeper_unit_cost_after != null
                ? `$${Number(preview.keeper_unit_cost_after).toFixed(2)}`
                : undefined
            }
          />
        </ul>
        {blockers.length > 0 ? (
          <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-amber-800 dark:text-amber-200">
            {blockers.map((b) => (
              <li key={b.code}>{b.message}</li>
            ))}
          </ul>
        ) : null}
        {error ? (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-red-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onConfirm}
            disabled={busy || blockers.length > 0}
          >
            {busy ? "Merging…" : "Merge & delete discard"}
          </button>
        </div>
      </div>
    </div>
  );
}
